import heicConvert from 'heic-convert/browser';

/**
 * Converts an ArrayBuffer into a Buffer (Node.js) or Uint8Array (browser).
 * @param {ArrayBuffer} arrayBuffer 
 * @returns {Uint8Array|Buffer}
 */
function toCompatibleBuffer(arrayBuffer) {
  if (typeof Buffer !== "undefined") {
    // Running in Node.js
    return Buffer.from(arrayBuffer);
  } else {
    // Running in browser
    return new Uint8Array(arrayBuffer);
  }
}

/**
 * Uses a fallback chain for compatibility:
 *   1. file.arrayBuffer() (modern & fast)
 *   2. FileReader (older but compatible)
 *   3. Response API (last resort)
 * 
 * @param {File|Blob} file 
 * @returns {Promise<Uint8Array|Buffer>}
 */
export async function getArrayBuffer(file) {
  let arrayBuffer;

  // Method 1: modern & optimal (arrayBuffer native)
  try {
    if (file.arrayBuffer) {
      arrayBuffer = await file.arrayBuffer();
    }
  } catch (err) {
    console.warn("[getBuffer] arrayBuffer() failed:", err);
  }

  // Method 2: fallback using FileReader (more compatible, slower)
  if (!arrayBuffer) {
    try {
      arrayBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    } catch (err) {
      console.warn("[getBuffer] FileReader failed:", err);
    }
  }

  // Method 3: last resort using Response API
  if (!arrayBuffer) {
    try {
      arrayBuffer = await new Response(file).arrayBuffer();
    } catch (err) {
      console.error("[getBuffer] All methods failed:", err);
      throw new Error("Could not retrieve buffer from file");
    }
  }

  return arrayBuffer;
}

/**
 * 
 * @param {*} resource 
 * @returns blob
 */
export async function previewHeicImage(resource, options = {}){
    let blob, inputBuffer;
    try {
        const arrayBuffer = await getArrayBuffer(resource);
        inputBuffer = toCompatibleBuffer(arrayBuffer); 
        const outputOptions = {
            buffer: inputBuffer, // Buffer containing HEIC file 
            format: 'JPEG', // Output format 
            quality: 1, // Quality scale: 0 (lowest) to 1 (highest)
            ...options
        }
        const outputBuffer = await heicConvert(outputOptions);
        blob = new Blob([outputBuffer], { type: 'image/jpeg' });
      
    } catch (error) {
        blob = inputBuffer ? new Blob([inputBuffer]) : null;
        console.error("Error converting HEIC image:", error);
    }
    return blob;
}

/**
 * 
 * @param {*} imageDownloaded 
 * @returns blob
 */
export function previewDownloadedHeicImage(imageDownloaded) {
    return previewHeicImage(imageDownloaded.Body);
}

/**
 * 
 * @param {*} file 
 * @returns converted file
 */
export async function convertHeicToJPEG(file, quality = 0.6) {
    try {
        const blob = await previewHeicImage(file, { quality });
        if (!blob) {
            console.warn("HEIC conversion failed, returning original file");
            return file;
        }
        const newFileName = file.name.replace(/\.\w+$/, '.jpeg');
        return new File([blob], newFileName, { type: "image/jpeg" });
    } catch (error) {
        console.error("HEIC conversion error:", error);
        return file;
    }
}

/**
 * 
 * @param {*} file 
 * @returns true or false
 */
export function isHEICFile(file) {
    return file.type === "image/heic" ||  /\.heic$/i.test(file.name);
}
